<template>
  <div class="color-input">
    <label v-if="props.label"
           class="label">
      {{ props.label }}
    </label>
    <div
      class="color-preview"
      :style="{ backgroundColor: canvasValue || '' }"
      @click="showCanvas = !showCanvas"
    />
    <div v-if="!props.collapsable || showCanvas"
         class="input-container">
      <v-color-picker
        v-model="canvasValue"
        class="color-picker"
        hide-inputs
        mode="hex"
        width="100%"
        @update:model-value="onCanvasChange"
      />
      <div class="color-input-text">
        <div class="color-input-text-label">HEX</div>

        <beva-text-field
          v-model="inputValue"
          class="color-input-text-field"
          :error-messages="errorMessages"
          prefix="#"
          size="sm"
          @on-change="onInputChange"
        />
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup>
  import { ref, withDefaults, watch, onMounted, defineOptions, nextTick } from 'vue';
  import { VColorPicker } from 'vuetify/components';

  import BevaTextField from '@/components/SharedComponents/Inputs/TextField.vue';

  type Color = string | null;

  defineOptions({
    inheritAttrs: false,
  });

  const props = withDefaults(
    defineProps<{
      label?: string
      collapsable?: boolean
      modelValue: Color
      errorMessages?: string | string[]
    }>(),
    {
      label: 'Choose color',
      collapsable: false,
      errorMessages: [],
    },
  );

  const emit = defineEmits<{
    (emit: 'on-change', value: Color): void
  }>();

  const DEFAULT_COLOR: string = '#095EA6';

  const canvasValue = ref<Color>('');
  const inputValue = ref<string>('');
  const showCanvas = ref<boolean>(false);

  watch(
    () => props.modelValue,
    (newValue: Color): void => {
      canvasValue.value = newValue;
      inputValue.value = (canvasValue.value as string).replace('#', '');
    },
  );

  const onCanvasChange = (newValue: Color): void => {
    canvasValue.value = newValue;
    inputValue.value = (newValue || '').replace('#', '');
    emit('on-change', newValue);
  };

  const onInputChange = (newValue: string): void => {
    inputValue.value = newValue;
    canvasValue.value = newValue ? '#' + newValue : '';
    emit('on-change', canvasValue.value);
  };

  onMounted((): void => {
    nextTick((): void => {
      onCanvasChange(props.modelValue || DEFAULT_COLOR);
    });
  });
</script>

<style lang="scss" scoped>
.color-input {
  display: flex; // flex
  flex-direction: column; // flex-col
  gap: 1rem; // gap-4

  .label {
    cursor: default; // cursor-default
    color: oklch(21% 0.034 264.665); // text-gray-900
    font-size: 1.25rem; // text-xl
    line-height: 1.25rem; // leading-5
  }

  .color-preview {
    height: 2.75rem; // h-11
    width: 2.75rem; // w-11
    cursor: pointer; // cursor-pointer
    border-radius: 9999px; // rounded-full
    border-width: 1px; // border
    border-color: oklch(92.8% 0.006 264.531); // border-gray-200
    box-shadow:
      0 1px 2px 0 rgba(0, 0, 0, 0.05),
      0 1px 3px 0 rgba(0, 0, 0, 0.1); // shadow-sm + shadow-black
  }
  .input-container {
    border-radius: 0.5rem; // rounded-xl
    border: 2px solid #e5e7eb; // border-2 border-gray-200
    padding: 1rem; // p-4

    :deep(.color-picker) {
      box-shadow: none !important; // !shadow-none

      .v-color-picker-preview__dot {
        display: none; // [&_.v-color-picker-preview\_\_dot]:hidden
      }

      .v-color-picker-preview__eye-dropper {
        display: none; // [&_.v-color-picker-preview\_\_eye-dropper]:hidden
      }

      .v-color-picker-preview__sliders {
        padding: 0; // [&_.v-color-picker-preview\_\_sliders]:p-0
      }
    }

    .color-input-text {
      margin-top: 0.25rem; // mt-1
      display: flex; // flex
      gap: 0.5rem; // gap-2
    }
    :deep(.color-input-text) {
      .v-field__field {
        &:dir(rtl) {
          flex-direction: row-reverse; // [&_.v-field__field]:rtl:flex-row-reverse
          padding: 0; // [&_.v-field__field]:rtl:p-0
        }
      }

      .v-field__input {
        &:dir(rtl) {
          direction: ltr;
          padding-inline-start: 0; // [&_.v-field\_\_input]:rtl:ps-0
        }
      }

      .v-text-field__prefix {
        padding-inline-end: 0.25rem; // [&_.v-text-field\_\_prefix]:pe-1

        &:dir(rtl) {
          padding-inline-start: 0.25rem !important; // [&_.v-text-field\_\_prefix]:rtl:!ps-1
          padding-inline-end: 1rem; // [&_.v-text-field\_\_prefix]:rtl:pe-4
        }
      }
    }

    .color-input-text-label {
      box-shadow: 0 1px 2px 0 rgba(0, 0, 0, 0.05); // shadow-sm
      height: 1.75rem; // h-7
      min-width: 4.25rem; // min-w-17
      border-radius: 0.25rem; // rounded-sm
      border: 1px solid oklch(21% 0.034 264.665 / .38); // border border-gray-900/.38
      padding: 0.25rem 0.5rem; // px-1 py-0.75
      font-size: 0.75rem; // text-xs
      line-height: 1.25rem; // leading-5
      color: #374151; // text-gray-700
    }
  }
}
</style>
